// components/Hero.jsx
import React from "react";
import { useNavigate } from 'react-router-dom';
import Lottie from "lottie-react";
import { motion } from "framer-motion";
import heroAnimation from "../assets/hero-animation.json";

const Hero = () => {
  const navigate = useNavigate();
  return (
    <section className="relative w-full overflow-hidden">
      {/* Extended Gradient Background that covers the entire viewport and beyond */}
      <div className="absolute inset-0 h-[200vh] bg-gradient-to-br from-white via-blue-50/40 via-purple-50/20 to-white dark:from-gray-900 dark:via-gray-800/40 dark:via-purple-900/10 dark:to-gray-900"></div>
      <div className="absolute inset-0 h-[200vh] bg-gradient-to-b from-transparent via-transparent via-blue-50/10 to-transparent"></div>
      
      {/* Main content container */}
      <div className="relative min-h-screen flex items-center justify-center">
        {/* Decorative Blob Background */}
        <motion.svg
          className="absolute top-[-150px] left-[-100px] w-[500px] sm:w-[700px] opacity-10 -z-10 text-blue-500"
          viewBox="0 0 600 600"
          xmlns="http://www.w3.org/2000/svg"
          animate={{ 
            rotate: [0, 360],
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            duration: 20, 
            repeat: Infinity, 
            ease: "linear" 
          }}
        >
          <g transform="translate(300,300)">
            <path
              d="M120,-135C160,-100,190,-60,185,-25C180,10,140,40,115,80C90,120,80,170,45,180C10,190,-50,160,-80,120C-110,80,-120,30,-130,-30C-140,-90,-150,-170,-115,-210C-80,-250,-10,-250,45,-220C100,-190,80,-170,120,-135Z"
              fill="currentColor"
            />
          </g>
        </motion.svg>

        {/* Additional Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden -z-10">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-blue-400 rounded-full opacity-20"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -100, 0],
                opacity: [0.2, 0.8, 0.2],
              }}
              transition={{
                duration: 3 + Math.random() * 4,
                repeat: Infinity,
                delay: Math.random() * 5,
              }}
            />
          ))}
        </div>

        <div className="container mx-auto px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-[80vh]">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="text-center lg:text-left space-y-8"
            >
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="mobile-title-margin text-3xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-bold leading-tight"
              >
                <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 bg-clip-text text-transparent">
                  We Craft Smart,
                </span>
                <br />
                <span className="bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 bg-clip-text text-transparent">
                  Stylish Websites
                </span>
                <br />
                <span className="text-gray-800 dark:text-white">
                  for All Businesses
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-lg sm:text-base md:text-lg lg:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto lg:mx-0"
              >
                From schools to stores — fast, responsive, and built to grow with you. 
                We transform your vision into stunning digital experiences.
              </motion.p>

              {/* <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 sm:gap-3 md:gap-4 justify-center lg:justify-start"
              >
                <motion.a
                  href="/contact"
                  className="inline-flex items-center justify-center px-4 py-4 sm:px-6 sm:py-3 md:px-8 md:py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm sm:text-sm md:text-base font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Get In Touch
                  <svg className="ml-1 w-3 h-3 sm:ml-2 sm:w-4 sm:h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </motion.a>
            
                <motion.button
                  onClick={() => {
                    navigate('/services');
                    // Scroll to pricing section after navigation
                    setTimeout(() => {
                      const pricingSection = document.querySelector('.pricing-section');
                      if (pricingSection) {
                        pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }, 300);
                  }}
                  className="inline-flex items-center justify-center px-4 py-4 sm:px-6 sm:py-3 md:px-8 md:py-4 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm sm:text-sm md:text-base font-semibold rounded-full hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  View Our Pricing
                </motion.button>
              </motion.div> */}

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="block md:hidden flex flex-col sm:flex-row gap-2 sm:gap-3 md:gap-4 justify-center lg:justify-start"
              > 
                <motion.a
                  href="/portfolio"
                  className="mobile-button-padding inline-flex items-center justify-center px-4 py-2 sm:px-6 sm:py-3 md:px-8 md:py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs sm:text-sm md:text-base font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Our Portfolio
                  <svg className="ml-1 w-3 h-3 sm:ml-2 sm:w-4 sm:h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </motion.a>
            
                <motion.button
                  // onClick={() => {
                  //   const processSection = document.querySelector('#process'); // or '.process-section'
                  //   if (processSection) {
                  //     processSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  //   }
                  //   // Scroll to pricing section after navigation
                  //   setTimeout(() => {
                  //     const pricingSection = document.querySelector('.pricing-section');
                  //     if (pricingSection) {
                  //       pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  //     }
                  //   }, 300);
                  // }}
                  onClick={() => {
                    navigate('/services');
                    // Scroll to pricing section after navigation
                    setTimeout(() => {
                      const pricingSection = document.querySelector('.pricing-section');
                      if (pricingSection) {
                        pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }, 300);
                  }}
                  className="mobile-button-padding inline-flex items-center justify-center px-4 py-2 sm:px-6 sm:py-3 md:px-8 md:py-4 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-xs sm:text-sm md:text-base font-semibold rounded-full hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  View Our Pricing
                </motion.button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="hidden md:flex flex-col sm:flex-row gap-2 sm:gap-3 md:gap-4 justify-center lg:justify-start"
              > 
                <motion.a
                  href="/portfolio"
                  className="inline-flex items-center justify-center px-4 py-2 sm:px-6 sm:py-3 md:px-8 md:py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs sm:text-sm md:text-base font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Our Portfolio
                  <svg className="ml-1 w-3 h-3 sm:ml-2 sm:w-4 sm:h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </motion.a>
            
                <motion.button
                  // onClick={() => {
                  //   const processSection = document.querySelector('#process'); // or '.process-section'
                  //   if (processSection) {
                  //     processSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  //   }
                  //   // Scroll to pricing section after navigation
                  //   setTimeout(() => {
                  //     const pricingSection = document.querySelector('.pricing-section');
                  //     if (pricingSection) {
                  //       pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  //     }
                  //   }, 300);
                  // }}
                  onClick={() => {
                    navigate('/services');
                    // Scroll to pricing section after navigation
                    setTimeout(() => {
                      const pricingSection = document.querySelector('.pricing-section');
                      if (pricingSection) {
                        pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }, 300);
                  }}
                  className="inline-flex items-center justify-center px-4 py-2 sm:px-6 sm:py-3 md:px-8 md:py-4 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-xs sm:text-sm md:text-base font-semibold rounded-full hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  View Our Pricing
                </motion.button>
              </motion.div>

              {/* Stats or Features */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="grid grid-cols-3 gap-2 sm:gap-4 md:gap-6 lg:gap-8 pt-4 sm:pt-6 md:pt-8 border-t border-gray-200 dark:border-gray-700"
              >
                {[
                  { number: "10+", label: "Projects Completed" },
                  { number: "100%", label: "Client Satisfaction" },
                  { number: "24/7", label: "Support Available" }
                ].map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-xl sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      {stat.number}
                    </div>
                    <div className="text-sm sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5 sm:mt-1">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right Lottie Animation */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="relative flex items-center justify-center"
            >
              <div className="relative w-full max-w-lg mx-auto">
                {/* Glowing background effect */}
                <div className="glow-bg absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full blur-3xl opacity-20 animate-pulse"></div>
                
                {/* Lottie Animation */}
                <div className="relative z-10">
                  <Lottie 
                    animationData={heroAnimation} 
                    loop={true}
                    className="hero-animation w-full h-auto"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
